import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/store/hooks';
import { api } from '@/store/api';
import type { DynamicSuggestionListItem } from '@/types/dynamicSuggestion';
import { SelectField } from '@gapartners/ui/select';
import {
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@gapartners/ui';

export function AISuggestionsCard() {
  const { t } = useTranslation();
  const contactId = useAppSelector(state => state.call.contactId);

  const hasActiveCall = Boolean(contactId);

  const [suggestions, setSuggestions] = useState<DynamicSuggestionListItem[]>(
    []
  );
  const [hasLoadedSuggestions, setHasLoadedSuggestions] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSuggestionId, setSelectedSuggestionId] = useState<
    string | null
  >(null);

  const {
    data: tagsData,
    isFetching: tagsLoading,
    isError: tagsError,
  } = api.useGetDynamicSuggestionTagsQuery(undefined, {
    skip: !hasActiveCall,
  });
  const [fetchSuggestions, { isFetching: listLoading }] =
    api.useLazyGetDynamicSuggestionsForCallQuery();

  const {
    data: detail,
    isFetching: detailLoading,
    isError: detailError,
  } = api.useGetDynamicSuggestionByIdQuery(selectedSuggestionId ?? '', {
    skip: !dialogOpen || !selectedSuggestionId,
  });

  useEffect(() => {
    setSuggestions([]);
    setHasLoadedSuggestions(false);
    setListError(null);
    setSelectedTags([]);
    setDialogOpen(false);
    setSelectedSuggestionId(null);
  }, [contactId]);

  const handleLoadSuggestions = useCallback(async () => {
    if (!contactId) return;
    setListError(null);
    try {
      const numericTags = selectedTags
        .map(tag => Number(tag))
        .filter(tag => Number.isFinite(tag));
      console.log('numericTags', numericTags);
      const result = await fetchSuggestions({
        contactId,
        tags: numericTags,
      }).unwrap();
      setSuggestions(result.suggestions ?? []);
      setHasLoadedSuggestions(true);
    } catch {
      setSuggestions([]);
      setHasLoadedSuggestions(false);
      setListError(t('components.contactCenter.dynamicSuggestionsLoadError'));
    }
  }, [contactId, fetchSuggestions, selectedTags, t]);

  const handleTileClick = (id: string) => {
    setSelectedSuggestionId(id);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSuggestionId(null);
  };

  const tagOptions = (tagsData?.tags ?? []).map(tag => ({
    value: tag.id,
    label: tag.name,
  }));

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, minWidth: '16rem' }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
          {t('components.contactCenter.aISuggestions')}
        </Typography>
        {!hasActiveCall ? (
          <Typography variant="body2" color="text.secondary">
            {t('components.contactCenter.aISuggestionsPlaceholder')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <SelectField
              label="Tags"
              options={tagOptions}
              multiple
              value={selectedTags}
              onChange={e => setSelectedTags(e.target.value as string[])}
              disabled={listLoading || tagsLoading || Boolean(tagsError)}
              fullWidth
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => void handleLoadSuggestions()}
              disabled={listLoading}
              sx={{ alignSelf: 'flex-start' }}
            >
              {listLoading
                ? t('components.contactCenter.loadingDynamicSuggestions')
                : t('components.contactCenter.loadDynamicSuggestions')}
            </Button>

            {listError && (
              <Typography variant="body2" color="error.main">
                {listError}
              </Typography>
            )}

            {hasLoadedSuggestions &&
            suggestions.length === 0 &&
            !listLoading &&
            !listError ? (
              <Typography variant="body2" color="text.secondary">
                {t('components.contactCenter.noDynamicSuggestions')}
              </Typography>
            ) : null}

            {suggestions.length > 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                {suggestions.map(s => (
                  <Paper
                    key={s.id}
                    variant="outlined"
                    onClick={() => handleTileClick(s.id)}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {s.title}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            ) : null}
          </Box>
        )}
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {detailLoading ? t('common.loading') : (detail?.title ?? '')}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                py: 4,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          )}
          {detailError && !detailLoading && (
            <Typography variant="body2" color="error.main">
              {t('components.contactCenter.dynamicSuggestionsLoadError')}
            </Typography>
          )}
          {!detailLoading && detail && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {detail.description ? (
                <Typography variant="body2" color="text.secondary">
                  {detail.description}
                </Typography>
              ) : null}
              {detail.content ? (
                <Box
                  sx={{
                    typography: 'body2',
                    color: 'text.primary',
                    '& p': { mt: 0, mb: 1 },
                    '& p:last-child': { mb: 0 },
                    '& a': { color: 'primary.main' },
                    '& ul, & ol': { pl: 2.5, my: 1 },
                    '& h1, & h2, & h3, & h4': {
                      mt: 2,
                      mb: 1,
                      fontWeight: 600,
                    },
                    '& h1:first-child, & h2:first-child, & h3:first-child': {
                      mt: 0,
                    },
                    '& img': { maxWidth: '100%', height: 'auto' },
                    '& pre, & code': {
                      fontFamily: 'monospace',
                      fontSize: '0.875em',
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: detail.content }}
                />
              ) : null}
              <Typography variant="caption" color="text.secondary">
                {[detail.status, detail.visibility, detail.access]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ gap: 1, flexWrap: 'wrap', px: 2, pb: 2 }}>
          {detail?.link ? (
            <Button
              variant="outlined"
              color="primary"
              onClick={() =>
                window.open(detail.link, '_blank', 'noopener,noreferrer')
              }
            >
              {t('components.contactCenter.dynamicSuggestionDetailLink')}
            </Button>
          ) : null}
          <Button onClick={handleCloseDialog} color="inherit">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
